import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { shallowRef, watch } from 'vue';
import fallbackShopHu from '~~/shop/app/locales/hu.json';
import fallbackDealerHu from '~~/dealer/app/locales/hu.json';
import ObjectCache from '~~/common/utils/ObjectCache';
import { PUSH_LOCALE_CODE_CHANGE } from '~~/common/plugins/aw-analytics';
import { useNotificationStore } from '~~/common/stores/notification';

// A flattened translation map. The message-tokens endpoint is a raw `.js` fetch
// (no generated swagger schema), and values may arrive as plain strings or as
// `{ text: ... }` objects, so each entry is treated defensively as `unknown`.
// Consumers (e.g. common/plugins/post-translation.ts) already handle both shapes.
type TranslationMap = Record<string, unknown>;

function isPlainObject (val: unknown): val is Record<string, unknown> {
  return Object.prototype.toString.call(val) === '[object Object]';
}

const translationCache = new ObjectCache<Promise<TranslationMap>>('translation', '__');

function flatten (
  obj: Record<string, unknown>,
  roots: string[] = [],
  sep = '.',
): TranslationMap {
  const initialTranslationMap: TranslationMap = {};
  const translationMapLevel = (Object.keys(obj)
    .reduce(
      (memo: TranslationMap, prop: string) => {
        const value = obj[prop];
        const childRoots = roots.concat([prop]);
        const trMap = (isPlainObject(value)
          ? flatten(value, childRoots)
          : {
            [childRoots.join(sep)]: value,
          }
        );
        const trMapMerged = Object.assign(
          {},
          memo,
          trMap,
        );
        return trMapMerged;
      },
      initialTranslationMap,
    )
  );
  return translationMapLevel;
}

export const useTranslationStore = defineStore('translation', () => {
  const nuxtApp = useNuxtApp();

  const hu = shallowRef<false | TranslationMap>(false);
  const en = shallowRef<false | TranslationMap>(false);
  const pl = shallowRef<false | TranslationMap>(false);

  const locales = {
    hu,
    en,
    pl,
  };

  type LocaleCode = keyof typeof locales;

  function isSupportedLocale (code: string): code is LocaleCode {
    return code in locales;
  }

  if (import.meta.client) {
    watch(
      nuxtApp.$i18n.locale,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          fetchTranslations({ localeCode: newVal });
        }
      },
    );
  }

  function setTranslations ({ values: translationResult, localeCode }: { values: TranslationMap; localeCode: LocaleCode }) {
    locales[localeCode].value = translationResult;
  }

  async function fetchTranslations ({ localeCode }: { localeCode: string }) {
    const { $awAnalytics, $logger, $messageTokenNetwork } = nuxtApp;
    try {
      try {
        // NOTE: import.meta.client is checked so only *changes* are tracked.
        if (import.meta.client) {
          $awAnalytics[PUSH_LOCALE_CODE_CHANGE]({ localeCode });
        }
      } catch (err) {
        $logger.error(err);
      }

      if (!isSupportedLocale(localeCode)) {
        $logger.warn(`Unsupported locale code "${localeCode}" — no translations available.`);
        return;
      }

      if (locales[localeCode].value) {
        return;
      }

      const cacheKey = ['translation', localeCode];
      translationCache.initCache(cacheKey, async () => {
        const translations = await $messageTokenNetwork.$get<TranslationMap>(`message-tokens.${localeCode}.js`);
        const filteredTranslations: TranslationMap = Object.fromEntries(Object.entries(translations).filter(([key]) => !(
          key.startsWith('admin.') ||
            key.startsWith('api.') ||
            key.startsWith('delivery_info.') ||
            key.startsWith('delivery_newsletter.') ||
            key.startsWith('mail.') ||
            key.startsWith('mainmenu.') ||
            key.startsWith('store_selector.')
        )));

        const fallbacksTokens: Record<'shop' | 'dealer', Record<string, Record<string, unknown>>> = {
          shop: {
            hu: fallbackShopHu,
          },
          dealer: {
            hu: fallbackDealerHu,
          },
        };

        const instanceTokens: Array<'shop' | 'dealer'> = __AW_IS_SHOP__ ? ['shop'] : ['shop', 'dealer'];

        const values: TranslationMap = {
          ...instanceTokens.reduce<TranslationMap>((prev, curr/* , idx */) => ({
            ...prev,
            ...flatten(fallbacksTokens[curr][localeCode] || {}),
          }), {}),
          ...filteredTranslations,
        };

        for (const i in values) {
          const entry = values[i];
          values[i] = (isPlainObject(entry) ? entry.text : undefined) || entry || '';
        }

        return values;
      });
      const values = await translationCache.getCache(cacheKey)?.value;
      if (!values) {
        $logger.warn(`No translations found for locale code "${localeCode}".`);
        return;
      }

      setTranslations({
        localeCode,
        values,
      });
    } catch (err) {
      $logger.error(err);
      const notificationStore = useNotificationStore();
      notificationStore.pushError({
        text: {
          title: `Could not fetch "${localeCode}" language tokens!`,
        },
      });
    }
  }

  return {
    hu,
    en,
    pl,
    fetchTranslations,
  };
});
